// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@pace/App/Components/Product/ProductPriceAndActions";

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import { IconMemo } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ListItem, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { ListItemProps } from "@pace/MobiusOverrides/Lists/ListItem";
import { UnorderedListProps } from "@pace/MobiusOverrides/Lists/UnorderedList";
import IconBadgeClearance from "@pace/XDL/Icons/IconBadgeClearance";
import { safeJsonParse, StyledBox } from "@pace/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";

export interface ProductDetailsActionsStyles {
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
    wrapper?: InjectableCss;
    shareContainer?: InjectableCss;
    actionsWrapper?: InjectableCss;
    noticeContainer?: InjectableCss;
    noticeHeader?: TypographyPresentationProps;
    loginNote?: TypographyPresentationProps;
    printLink?: LinkPresentationProps;
    shareLink?: ProductShareLinkStyles;
    noticeItems?: ListItemProps;
    noticeList?: UnorderedListProps;
}

export const productDetailsActionsStyles: ProductDetailsActionsStyles = {};

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated: xIsAuthenticated(state),
});

const styles = productDetailsActionsStyles;

type Props = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const ProductDetailsActions: FC<Props> = ({ productContext, isAuthenticated }) => {
    const externalDoc = safeJsonParse(productContext.product?.properties?.misExtDesc || "[]");
    const externalNotes = Array.isArray(externalDoc) ? externalDoc : []; /* to avoid invalid json array */
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product-details-actions">
            {/* Email & Print */}
            <StyledWrapper {...styles.shareContainer}>
                <ProductShareLink extendedStyles={styles.shareLink} text={translate("Email")} />
                <Link {...styles.printLink} onClick={openPrintDialog}>
                    {translate("Print")}
                </Link>
            </StyledWrapper>

            {/* Actions box */}
            <StyledBox {...styles.actionsWrapper}>
                {/* External Docs box */}
                {!!externalNotes?.length && (
                    <StyledBox {...styles.noticeContainer}>
                        <Typography {...styles.noticeHeader}>
                            <IconMemo src={IconBadgeClearance} size={14} />
                            {translate("Notice")}
                        </Typography>
                        <UnorderedList {...styles.noticeList}>
                            {externalNotes.map((note: string, index: number) => (
                                <ListItem key={`note-${index}`} {...styles.noticeItems}>
                                    {parse(transformContentHtml(note), parserOptions)}
                                </ListItem>
                            ))}
                        </UnorderedList>
                    </StyledBox>
                )}
                {!isAuthenticated && (
                    <Typography {...styles.loginNote}>
                        {siteMessage("Pace_Price_Availability_Accessible_On_Login")}
                    </Typography>
                )}
                <ProductPriceAndActions
                    showAddToList={true}
                    showAvailability={isAuthenticated}
                    showPrice={true}
                    showAddToCartActions={true}
                    extendedStyles={styles.productPriceAndActionsStyle}
                    showInfo={false}
                    isPdp={true}
                />
            </StyledBox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductDetailsActions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
